import React, { useEffect, useMemo } from "react";
import { Box, CardContent, Grid } from "@mui/material";
import { searchEmailTemplates } from "../../../../../axios/api/services";
import {
  BorderGreyBox,
  CardSpg,
  CreatedDateFilter,
  handleSubmitValidationFail,
  isNotEmpty,
  NameEnFilter,
  NameFilter,
  useReactHookForm,
} from "@duosoftbg/nacid-components";
import useSearchTableControl from "../../../../../hooks/portal/search/useSearchTableControl";
import SearchFilters from "../../../common/search/filters/SearchFilters";
import { SEARCH_FILTERS_GROUP } from "../../../../../config/search/filters/groupsConfig";
import { FormProvider } from "react-hook-form";
import { EmailTemplateFilterDetails } from "../../../../../types/emailTemplateTypes";
import { createEmailTemplateFilterValidationSchema } from "../../../../../yup/schema/email/template/templateFilterValidationSchema";
import { emailTemplateFilterInitialValues } from "../../../../../init/emailTemplateFilterInitialValues";
import { useTranslation } from "react-i18next";
import EmailTemplatesList from "./EmailTemplatesList";
import { useStore } from "react-redux";
import SubjectFilter from "../../../common/search/filters/definition/SubjectFilter";
import TextFilter from "../../../common/search/filters/definition/TextFilter";
import LastUpdateDateFilter from "../../../common/search/filters/definition/LastUpdateDateFilter";
import { RootState } from "../../../../../store/redux/store";

const EmailTemplatesFilters = () => {
  return (
    <Grid container spacing={5} px={2} py={6}>
      <NameFilter />
      <NameEnFilter />
      <SubjectFilter />
      <TextFilter />
      <CreatedDateFilter />
      <LastUpdateDateFilter />
    </Grid>
  );
};

const EmailTemplates = () => {
  const { t } = useTranslation();
  const store = useStore();
  const group = SEARCH_FILTERS_GROUP.EMAIL_TEMPLATES;

  const { methods } = useReactHookForm<EmailTemplateFilterDetails>({
    defaultValues: emailTemplateFilterInitialValues,
    validationSchema: createEmailTemplateFilterValidationSchema,
  });

  useEffect(() => {
    const filtersData = (store.getState() as RootState).SearchData.portalSearchTable[group].filtersData;
    if (isNotEmpty(filtersData)) {
      methods.reset(filtersData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const control = useSearchTableControl({
    group,
    methods,
    initialValues: emailTemplateFilterInitialValues,
    filterData: searchEmailTemplates,
    callAfterResetFilters: true,
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(
          () => control.handleSubmitFilters(),
          () => handleSubmitValidationFail(methods.formState, t),
        )}
      >
        <CardSpg my={4} style={{ overflow: "visible" }}>
          <CardContent style={{ position: "relative" }}>
            <Box>
              <BorderGreyBox>
                <SearchFilters
                  isSubmitBtnDisabled={control.isSubmitBtnDisabled}
                  isResetBtnDisabled={control.isResetBtnDisabled}
                  handleResetFilters={control.handleResetFilters}
                >
                  {useMemo(
                    () => (
                      <EmailTemplatesFilters />
                    ),
                    [],
                  )}
                </SearchFilters>
              </BorderGreyBox>
              {control.showList && (
                <EmailTemplatesList
                  records={control.records}
                  total={control.total}
                  isLoading={control.isLoading}
                  onPageOrOrderChange={control.handlePageOrOrderChange}
                  blockTable={control.blockTable}
                  group={group}
                  filterFn={control.fetch}
                />
              )}
            </Box>
          </CardContent>
        </CardSpg>
      </form>
    </FormProvider>
  );
};

export default EmailTemplates;
